import type { LifecycleState } from '@hospasense/contracts';
import { LIFECYCLE_LABELS } from '@hospasense/contracts';
import type { ReactNode } from 'react';

type Tone = 'neutral' | 'success' | 'warning' | 'error' | 'info' | 'accent';

const TONE_CLASSES: Record<Tone, string> = {
  neutral: 'bg-bg-subtle text-text-secondary',
  success: 'bg-success-bg text-success',
  warning: 'bg-warning-bg text-warning',
  error: 'bg-error-bg text-error',
  info: 'bg-info-bg text-info',
  accent: 'bg-accent-muted text-accent-fg',
};

export function Badge({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-lg px-2 py-0.5 text-xs font-medium ${TONE_CLASSES[tone]}`}
    >
      {children}
    </span>
  );
}

/** Meaning never travels by colour alone: dot + label survive greyscale. */
export function StatusPill({ tone, label }: { tone: Tone; label: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${TONE_CLASSES[tone]}`}
    >
      <span className="size-1.5 rounded-full bg-current" aria-hidden="true" />
      {label}
    </span>
  );
}

const LIFECYCLE_TONES: Record<LifecycleState, Tone> = {
  prospect: 'neutral',
  evaluation: 'info',
  onboarding: 'warning',
  live: 'success',
  suspended: 'error',
  churned: 'neutral',
};

export function LifecyclePill({ state }: { state: LifecycleState }) {
  return <StatusPill tone={LIFECYCLE_TONES[state]} label={LIFECYCLE_LABELS[state]} />;
}

export function HealthPill({ health }: { health: 'healthy' | 'degraded' | 'stale' | 'unknown' }) {
  /**
   * 'stale' is warning, not neutral. An installation that stopped reporting is
   * an active problem — the last thing it said was "healthy", and repeating
   * that quietly in grey is how a dead fleet looks fine on a dashboard.
   */
  const tone =
    health === 'healthy'
      ? 'success'
      : health === 'degraded' || health === 'stale'
        ? 'warning'
        : 'neutral';
  const label =
    health === 'stale' ? 'Stale (not reporting)' : health.charAt(0).toUpperCase() + health.slice(1);
  return <StatusPill tone={tone} label={label} />;
}
